import phoneNumberRegEx from '@@/common/constants/phoneNumberRegEx';

/**
 * Converts a phone number to a list of only digits.
 *
 * @example
 * digitsOnly('123-456-7890'); // '1234567890'
 */
export const digitsOnly = (number: string) => number.replace(/\D/g, '');

export type TParsedPhoneNumber = {
  intl: string;
  areaCode: string;
  firstThree: string;
  lastFour: string;
};

/** Parse a string representation of a phone number into separate parts. */
export const parsePhoneNumber = (number: string): TParsedPhoneNumber | null => {
  const match = digitsOnly(number).match(phoneNumberRegEx);

  if (!match) {
    return null;
  }

  const [, intl, areaCode, firstThree, lastFour] = match;

  return { intl, areaCode, firstThree, lastFour };
};

/** Converts a string representation of a phone number to a standardized format.
 *
 * @example
 * formatPhoneNumber('1234567890');     //   '123-456-7890'
 * formatPhoneNumber('123-456-7890');   //   '123-456-7890'
 * formatPhoneNumber('(123) 456-7890')  //   '123-456-7890'
 * formatPhoneNumber('123.456.7890')    //   '123-456-7890'
 * formatPhoneNumber('1-123-456-7890')  // '1-123-456-7890'
 * formatPhoneNumber('taco')            // ''
 *
 * @returns
 * Formatted number if given valid input, otherwise an empty string.
 */
export function formatPhoneNumber(
  number: string | ReturnType<typeof parsePhoneNumber>
) {
  const parsed = typeof number === 'string' ? parsePhoneNumber(number) : number;

  if (!parsed) {
    return '';
  }

  const { intl, areaCode, firstThree, lastFour } = parsed;

  return `${intl ? `${intl}-` : ''}${areaCode}-${firstThree}-${lastFour}`;
}
